import {
  PracticeTemplateDetail,
  ProgramBenchmarkName,
  ProgramTask,
  TaskDataTraitStatus,
} from '@cibo/core'
import { Dialog, Stack } from '@mui/material'
import { map, pipe, uniq } from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { useProgramEngagementContext } from '../../../../../context'
import { pickDetailValue } from '../../../models/TaskDataModel'
import { TemplateDeleteConfirmDialog } from './TemplateDeleteConfirmDialog'
import { FieldTraitTemplateEditorMain } from './FieldTraitTemplateEditorMain'
import { TemplateEditorContext } from './TemplateEditorContext'
import { TemplateEditorSidebar } from './TemplateEditorSidebar'
import { TemplateUnsavedGuard } from './TemplateUnsavedGuard'
import { useTemplateEditorState } from './useTemplateEditorState'

interface FieldTraitTemplateEditorModalProps {
  open: boolean
  onClose: () => void
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  initialTemplateId?: string
}

export const FieldTraitTemplateEditorModal = ({
  open,
  onClose,
  task,
  initialTemplateId,
}: FieldTraitTemplateEditorModalProps) => {
  const { engagement, program, fieldModels } = useProgramEngagementContext()
  const programId = engagement.data?.programId ?? ''
  const taskId = task.id
  const [shouldFocusName, setShouldFocusName] = useState(false)

  const benchmark = program.data?.benchmarks?.find(
    b => b.benchmark === task.rule.params?.benchmarkName
  )

  const allTraitIds: string[] = useMemo(
    () =>
      pipe(
        map((d: { traitId: string }) => d.traitId),
        uniq
      )(benchmark?.details ?? []) as string[],
    [benchmark?.details]
  )

  const createEntriesForTrait = useCallback(
    (traitId: string): PracticeTemplateDetail[] => [{ traitId, year: 0 }],
    []
  )

  const {
    selectedId,
    draftName,
    setDraftName,
    draftDetails,
    setIsDirty,
    ownerScope,
    nameError,
    setNameError,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showUnsavedGuard,
    setShowUnsavedGuard,
    sidebarTemplates,
    isLoadingTemplates,
    selectedTemplate,
    canEdit,
    canDelete,
    availableTraitIds,
    isAdvisorOrAdmin,
    isDeleting,
    isSaving,
    isDirty,
    authUserId,
    farmerUserId,
    requestNavigate,
    requestClose,
    handleUnsavedDiscard,
    handleValueSave,
    handleValueDelete,
    handleAddPractice,
    handlePrePopulate,
    handleSave,
    confirmName,
    handleOwnerScopeChange,
    handleDeleteConfirm,
  } = useTemplateEditorState({
    open,
    onClose,
    programId,
    taskId,
    initialTemplateId,
    allTraitIds,
    createEntriesForTrait,
  })

  const onPrePopulate = useCallback(
    (fieldId: string) => {
      const field = fieldModels.data?.items?.find(f => f.id === fieldId)
      if (!field || !benchmark) return
      const newDetails: PracticeTemplateDetail[] = allTraitIds
        .map(traitId => {
          const fieldDetail = field.findDetail({ traitId })
          if (!fieldDetail) return null
          const detailValueKey =
            task.rule.params?.traits?.find(t => t.traitId === traitId)?.detailValueKey ?? 'result'
          const detailValue = pickDetailValue(fieldDetail, detailValueKey)
          if (!Object.keys(detailValue).length) return null
          return { traitId, year: 0, ...detailValue }
        })
        .filter(Boolean) as PracticeTemplateDetail[]
      handlePrePopulate(newDetails)
    },
    [fieldModels.data?.items, benchmark, allTraitIds, task.rule.params?.traits, handlePrePopulate]
  )

  const hasPractices = draftDetails.length > 0
  const hasName = !!draftName.trim()
  const canSave = isDirty && hasPractices && !isSaving && hasName

  const handleSaveAndClose = async () => {
    const success = await handleSave()
    if (success) {
      setShowUnsavedGuard(false)
      onClose()
    }
  }

  const handleNameAndSave = useCallback(() => {
    setShowUnsavedGuard(false)
    setShouldFocusName(true)
  }, [setShowUnsavedGuard])

  return (
    <TemplateEditorContext.Provider
      value={{ canSave, isSaving, hasName, onNameAndSave: handleNameAndSave }}
    >
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth="lg"
        fullWidth
        data-testid="template-editor-modal"
      >
        <Stack direction="row" sx={{ height: '80vh', overflow: 'hidden' }}>
          <TemplateEditorSidebar
            templates={sidebarTemplates}
            isLoading={isLoadingTemplates}
            selectedId={selectedId}
            onSelect={requestNavigate}
            draftName={draftName}
            onDraftNameChange={name => {
              setDraftName(name)
              setIsDirty(true)
              setNameError(null)
            }}
            nameError={nameError}
            canEdit={canEdit}
            onSave={handleSave}
            isAdvisorOrAdmin={isAdvisorOrAdmin}
            farmerUserId={farmerUserId}
            authUserId={authUserId}
          />
          <FieldTraitTemplateEditorMain
            task={task}
            draftDetails={draftDetails}
            onValueSave={handleValueSave}
            onValueDelete={handleValueDelete}
            onAddPractice={handleAddPractice}
            onPrePopulate={onPrePopulate}
            availableTraitIds={availableTraitIds}
            fields={fieldModels.data?.items ?? []}
            canEdit={canEdit}
            canChangeOwnerScope={isAdvisorOrAdmin}
            canDelete={canDelete}
            isDeleting={isDeleting}
            isSaving={isSaving}
            isDirty={isDirty}
            draftName={draftName}
            nameError={nameError}
            selectedId={selectedId}
            onDraftNameChange={name => {
              setDraftName(name)
              setIsDirty(true)
              setNameError(null)
            }}
            onConfirmName={confirmName}
            onDelete={() => setShowDeleteConfirm(true)}
            onClose={requestClose}
            onSave={handleSave}
            ownerScope={ownerScope}
            onOwnerScopeChange={handleOwnerScopeChange}
            shouldFocusName={shouldFocusName}
            onShouldFocusNameHandled={() => setShouldFocusName(false)}
          />
        </Stack>
      </Dialog>

      <TemplateDeleteConfirmDialog
        open={showDeleteConfirm}
        name={selectedTemplate?.name}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <TemplateUnsavedGuard
        open={showUnsavedGuard}
        selectedId={selectedId}
        onKeepEditing={() => setShowUnsavedGuard(false)}
        onDiscard={handleUnsavedDiscard}
        onSaveAndClose={handleSaveAndClose}
      />
    </TemplateEditorContext.Provider>
  )
}
